import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import {
  fetchOneAuthor,
  addEditAuthor,
  deleteAuthor,
  clearAuthors,
} from "./../../actions/authorsActions";
import { uploadImage, clearMedia } from "./../../actions/mediaActions";
import Swal from "sweetalert2";
import LoadingBackdrop from "../Common/LoadingBackdrop";
import ImageContainerPreview from "../Common/ImageContainerPreview";
import Editor from "../Common/Editor";
import AuthorCaptionAliases from "../AuthorCaptionAliases/AuthorCaptionAliases";

const AddEditAuthor = forwardRef(({ isEdit, selectedId }, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentAuthor = useSelector((state) => state.authors.currentAuthor);
  const [loading, setLoading] = useState(false);
  const editorRef = useRef(null);

  const [state, setState] = useState({
    author_name: "",
    author_title: "",
    author_image: "",
    author_signature: "",
    author_signature_preview: null,
    author_bio: '<p style="font-family: Asap-Regular; font-size: 16px; color: #545454;"><br></p>',
    author_facebook_url: "",
    author_twitter_url: "",
    author_instagram_url: "",
    author_image_preview: null,
    author_is_active: 1,
    isSetToEdit: false,
  });

  // Set author for editing when data is loaded — verify ID matches to avoid stale data
  useEffect(() => {
    if (isEdit && currentAuthor && String(currentAuthor.author_id) === String(selectedId)) {
      setState(prev => ({
        ...prev,
        ...currentAuthor,
        // Preserve defaults for null fields
        author_bio: currentAuthor.author_bio || prev.author_bio,
        author_image: currentAuthor.author_image || "",
        author_signature: currentAuthor.author_signature || "",
        author_facebook_url: currentAuthor.author_facebook_url || "",
        author_twitter_url: currentAuthor.author_twitter_url || "",
        author_instagram_url: currentAuthor.author_instagram_url || "",
        isSetToEdit: true,
      }));
    }
  }, [isEdit, currentAuthor, selectedId]);

  // Clear currentAuthor on unmount to prevent stale data
  useEffect(() => {
    return () => {
      dispatch(clearAuthors());
    };
  }, [dispatch]);

  const handleBioChange = (html) => {
    setState(prev => ({ ...prev, author_bio: html }));
  };

  const handleSelectionChange = (text) => {
    // Handle selection changes if needed
  };

  const submit = useCallback(() => {
    if (!state.author_name) {
      Swal.fire("Error", "Please enter author name", "error");
      return;
    }

    try {
      const authorData = {
        author_name: state.author_name,
        author_title: state.author_title,
        author_image: state.author_image,
        author_signature: state.author_signature,
        author_bio: state.author_bio,
        author_facebook_url: state.author_facebook_url,
        author_twitter_url: state.author_twitter_url,
        author_instagram_url: state.author_instagram_url,
        author_is_active: state.author_is_active,
        ...(isEdit && { author_id: selectedId }),
      };

      dispatch(addEditAuthor(JSON.stringify(authorData), () => navigate('/authors')));
    } catch (error) {
      console.error("Error saving author:", error);
      Swal.fire("Error", "Failed to save author", "error");
    }
  }, [state, isEdit, selectedId, dispatch, navigate]);

  // Fetch single author when editing
  useEffect(() => {
    if (!isEdit || !selectedId) return;

    setLoading(true);
    dispatch(fetchOneAuthor(selectedId))
      .catch(error => {
        console.error("Error fetching author:", error);
        Swal.fire({
          icon: "warning",
          text: "Error finding selected Author",
        });
      })
      .finally(() => setLoading(false));
  }, [dispatch, isEdit, selectedId]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setState(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (state.author_image_preview) {
      window.URL.revokeObjectURL(state.author_image_preview);
    }

    setState(prev => ({
      ...prev,
      author_image_preview: window.URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file))
      .then((response) => {
        setState(prev => ({
          ...prev,
          author_image: response.data.image_url,
        }));
        dispatch(clearMedia());
      });
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (state.author_signature_preview) {
      window.URL.revokeObjectURL(state.author_signature_preview);
    }

    setState(prev => ({
      ...prev,
      author_signature_preview: window.URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file))
      .then((response) => {
        setState(prev => ({
          ...prev,
          author_signature: response.data.image_url,
        }));
        dispatch(clearMedia());
      });
  };

  const handleRemoveImage = (e) => {
    const { name } = e.currentTarget;
    if (name === 'author_image') {
      if (state.author_image_preview) {
        window.URL.revokeObjectURL(state.author_image_preview);
      }
      setState(prev => ({
        ...prev,
        author_image: '',
        author_image_preview: null,
      }));
    } else if (name === 'author_signature') {
      if (state.author_signature_preview) {
        window.URL.revokeObjectURL(state.author_signature_preview);
      }
      setState(prev => ({
        ...prev,
        author_signature: '',
        author_signature_preview: null,
      }));
    }
  };

  const handleDelete = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteAuthor(selectedId, () => navigate("/authors")));
      }
    });
  };

  useImperativeHandle(ref, () => ({
    submit
  }), [submit]);

  if (isEdit && loading) {
    return <LoadingBackdrop open={true} />;
  }

  return (
    <Box component="form" autoComplete="off" sx={{ width: '100%' }}>
      <Grid container spacing={3} sx={{ width: '100%', m: 0 }}>
        <Grid sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>Author Information</Typography>
          <TextField
            fullWidth
            label="Author Name"
            name="author_name"
            value={state.author_name}
            onChange={handleFieldChange}
            required
            sx={{ mb: 3 }}
          />
          <TextField
            fullWidth
            label="Author Title"
            name="author_title"
            value={state.author_title}
            onChange={handleFieldChange}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle1" sx={{ mb: 2, mt: 4 }}>Social Media Links</Typography>
          <TextField
            fullWidth
            label="Facebook URL"
            name="author_facebook_url"
            value={state.author_facebook_url}
            onChange={handleFieldChange}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="X URL"
            name="author_twitter_url"
            value={state.author_twitter_url}
            onChange={handleFieldChange}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Instagram URL"
            name="author_instagram_url"
            value={state.author_instagram_url}
            onChange={handleFieldChange}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle1" sx={{ mb: 2, mt: 4 }}>Images</Typography>
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Profile Picture
              </Typography>
              <ImageContainerPreview
                title="Profile Picture"
                image={state.author_image_preview || state.author_image}
                name="author_image"
                handleImageUpload={handleImageUpload}
                handleRemove={handleRemoveImage}
                showCopyUrlButton={true}
                sx={{ padding: 1, background: '#FFFCF5', height: 90, objectFit: 'contain' }}
              />
            </Box>
            <Box>
              <Typography variant="h6" gutterBottom>
                Signature
              </Typography>
              <ImageContainerPreview
                title="Signature"
                image={state.author_signature_preview || state.author_signature}
                name="author_signature"
                handleImageUpload={handleSignatureUpload}
                handleRemove={handleRemoveImage}
                showCopyUrlButton={true}
                sx={{ padding: 1, background: '#FFFCF5', height: 90, objectFit: 'contain' }}
              />
            </Box>
          </Box>

          <Typography variant="subtitle1" sx={{ mb: 2, mt: 4 }}>Author Bio</Typography>
          <Box sx={{
            '& .fr-wrapper': {
              height: '400px',
              fontFamily: 'Asap-Regular',
              fontSize: '16px',
              color: '#545454',
            },
            '& .fr-element': {
              fontFamily: 'Asap-Regular',
              fontSize: '16px',
              color: '#545454',
              '& p': {
                margin: 0,
                padding: 0,
              }
            },
            '& .fr-toolbar': {
              borderTopLeftRadius: '4px',
              borderTopRightRadius: '4px',
              borderColor: 'rgba(0, 0, 0, 0.23)',
            },
            '& .fr-wrapper': {
              borderBottomLeftRadius: '4px',
              borderBottomRightRadius: '4px',
              borderColor: 'rgba(0, 0, 0, 0.23)',
            }
          }}>
            <Editor
              ref={editorRef}
              defaultValue={state.author_bio}
              onTextChange={handleBioChange}
              onSelectionChange={handleSelectionChange}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Per-author caption aliases (Phase 4.5). Renders inline so
          captions ops can fix Whisper mishearings without leaving the
          Author edit page. Hidden for admins without manage_captions. */}
      <AuthorCaptionAliases authorId={isEdit ? selectedId : null} />

      {isEdit && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            color="error"
            onClick={handleDelete}
            startIcon={<Delete />}
            variant="contained"
          >
            Delete Author
          </Button>
        </Box>
      )}
      <LoadingBackdrop open={loading} />
    </Box>
  );
});

AddEditAuthor.displayName = 'AddEditAuthor';

export default AddEditAuthor;
